import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PROPERTY_PURPOSES,
  PROPERTY_TYPES,
  addProperty,
  getPropertyById,
  updateProperty,
  type PropertyPhoto,
  type PropertyPurpose,
  type PropertyType,
} from '@/services/properties';
import {
  deletePhotoFromDrive,
  ensurePropertyFolder,
  getAccessToken,
  isGoogleDriveAuthorized,
  preloadGoogleIdentity,
  uploadPhotoToDrive,
} from '@/lib/googleDrive';

const CITIES = ['Chapecó', 'Xanxerê', 'São Miguel do Oeste', 'Maravilha', 'Palmitos'];

const featureFields = [
  { key: 'bedrooms' as const, label: 'Quartos', icon: 'ri-hotel-bed-line' },
  { key: 'bathrooms' as const, label: 'Banheiros', icon: 'ri-showers-line' },
  { key: 'parking' as const, label: 'Vagas de garagem', icon: 'ri-car-line' },
];

function formatPrice(value: number, purpose: PropertyPurpose): string {
  const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  return purpose === 'Locação' ? `${formatted}/mês` : formatted;
}

export default function PropertyForm() {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [type, setType] = useState<PropertyType>('Casa');
  const [purpose, setPurpose] = useState<PropertyPurpose>('Venda');
  const [city, setCity] = useState(CITIES[0]);
  const [neighborhood, setNeighborhood] = useState('');
  const [location, setLocation] = useState('');
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [parking, setParking] = useState(0);
  const [area, setArea] = useState(0);
  const [priceValue, setPriceValue] = useState(0);
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<PropertyPhoto[]>([]);
  const [driveFolderId, setDriveFolderId] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  const [driveAuthorized, setDriveAuthorized] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);

  const isTerreno = type === 'Terreno';
  const priceLabel = useMemo(() => formatPrice(priceValue, purpose), [priceValue, purpose]);

  useEffect(() => {
    preloadGoogleIdentity();
    setDriveAuthorized(isGoogleDriveAuthorized());
  }, []);

  useEffect(() => {
    setError('');

    if (!id) {
      // Modo "novo imóvel": limpa qualquer dado deixado por uma edição anterior
      // (o componente é reaproveitado pelo React Router ao navegar entre rotas).
      setTitle('');
      setType('Casa');
      setPurpose('Venda');
      setCity(CITIES[0]);
      setNeighborhood('');
      setLocation('');
      setBedrooms(0);
      setBathrooms(0);
      setParking(0);
      setArea(0);
      setPriceValue(0);
      setDescription('');
      setPhotos([]);
      setDriveFolderId(null);
      setActive(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      const property = await getPropertyById(id);
      if (!property) {
        setError('Imóvel não encontrado.');
        setLoading(false);
        return;
      }
      setTitle(property.title);
      setType(property.type);
      setPurpose(property.purpose);
      setCity(property.city);
      setNeighborhood(property.neighborhood);
      setLocation(property.location);
      setBedrooms(property.bedrooms);
      setBathrooms(property.bathrooms);
      setParking(property.parking);
      setArea(property.area);
      setPriceValue(property.priceValue);
      setDescription(property.description);
      setPhotos(property.photos);
      setDriveFolderId(property.driveFolderId);
      setActive(property.active);
      setLoading(false);
    })();
  }, [id]);

  const handleConnectDrive = async () => {
    setError('');
    setConnectingDrive(true);
    try {
      await getAccessToken();
      setDriveAuthorized(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao conectar ao Google Drive.');
    } finally {
      setConnectingDrive(false);
    }
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    if (!title.trim()) {
      setError('Preencha o título do imóvel antes de enviar fotos (o título define o nome da pasta no Google Drive).');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const folderId = driveFolderId ?? (await ensurePropertyFolder(title));
      setDriveFolderId(folderId);

      for (const file of Array.from(fileList)) {
        const uploaded = await uploadPhotoToDrive(file, folderId);
        setPhotos((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar fotos para o Google Drive.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (photo: PropertyPhoto) => {
    setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    try {
      await deletePhotoFromDrive(photo.id);
    } catch {
      // A foto pode já ter sido removida do Drive manualmente; ignorar falha.
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!title.trim() || !location.trim() || !city || !neighborhood.trim()) {
      setError('Preencha título, cidade, bairro e localização.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        type,
        purpose,
        city,
        neighborhood: neighborhood.trim(),
        location: location.trim(),
        bedrooms: isTerreno ? 0 : bedrooms,
        bathrooms: isTerreno ? 0 : bathrooms,
        parking: isTerreno ? 0 : parking,
        area,
        price: priceLabel,
        priceValue,
        description: description.trim(),
        photos,
        driveFolderId,
        active,
      };

      if (isEditing && id) {
        await updateProperty(id, payload);
      } else {
        await addProperty(payload);
      }
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao salvar o imóvel.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background-100 flex items-center justify-center">
        <i className="ri-loader-4-line text-2xl text-foreground-500 animate-spin"></i>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-100">
      <header className="bg-secondary-900 px-4 md:px-8 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          aria-label="Voltar"
          className="w-8 h-8 flex items-center justify-center text-background-300 hover:text-background-50 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <span className="text-background-50 font-semibold text-sm">
          {isEditing ? 'Editar imóvel' : 'Novo imóvel'}
        </span>
      </header>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-3 mb-6">{error}</p>
        )}

        {/* Basic info */}
        <section className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 mb-6">
          <h2 className="text-sm font-bold text-secondary-900 mb-4">Informações básicas</h2>

          <div className="mb-4">
            <label className="text-xs font-semibold text-foreground-700 block mb-2">Título do anúncio</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Casa à venda no Jardim Itália"
              className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <p className="text-[11px] text-foreground-500 mt-1.5">
              Esse título também nomeia a subpasta de fotos no Google Drive.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Finalidade</label>
              <select
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as PropertyPurpose)}
                className="w-full appearance-none bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {PROPERTY_PURPOSES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Tipo de imóvel</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as PropertyType)}
                className="w-full appearance-none bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Cidade</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full appearance-none bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Bairro</label>
              <input
                type="text"
                required
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
                placeholder="Ex: Jardim Itália"
                className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="text-[11px] text-foreground-500 mt-1.5">
                Usado nos filtros de busca do site — mantenha a mesma grafia entre imóveis do mesmo bairro.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Preço (R$)</label>
              <input
                type="number"
                min={0}
                required
                value={priceValue || ''}
                onChange={(e) => setPriceValue(Number(e.target.value))}
                placeholder="0"
                className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="text-[11px] text-foreground-500 mt-1.5">Exibido como: {priceLabel}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground-700 block mb-2">Localização (exibida no anúncio)</label>
              <input
                type="text"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ex: Chapecó/SC - Bairro Jardim Itália"
                className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 mb-6">
          <h2 className="text-sm font-bold text-secondary-900 mb-4">Características</h2>

          {isTerreno && (
            <p className="text-xs text-foreground-500 bg-background-100 rounded-md px-3 py-2 mb-4">
              Terrenos não exibem quartos, banheiros ou vagas de garagem.
            </p>
          )}

          <div className={`grid gap-4 mb-4 ${isTerreno ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {!isTerreno &&
              featureFields.map(({ key, label, icon }) => {
                const value = key === 'bedrooms' ? bedrooms : key === 'bathrooms' ? bathrooms : parking;
                const setValue = key === 'bedrooms' ? setBedrooms : key === 'bathrooms' ? setBathrooms : setParking;
                return (
                  <div key={key}>
                    <label className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                      <i className={`${icon} text-primary-500`}></i>
                      {label}
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={value}
                      onChange={(e) => setValue(Number(e.target.value))}
                      className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                );
              })}

            <div>
              <label className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <i className="ri-shape-line text-primary-500"></i>
                Área (m²)
              </label>
              <input
                type="number"
                min={0}
                required
                value={area || ''}
                onChange={(e) => setArea(Number(e.target.value))}
                className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-foreground-700 block mb-2">Descrição</label>
            <textarea
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o imóvel: ambientes, diferenciais, localização, condições..."
              className="w-full bg-background-100 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            />
          </div>
        </section>

        {/* Photos */}
        <section className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 mb-6">
          <h2 className="text-sm font-bold text-secondary-900 mb-1">Fotos do imóvel</h2>
          <p className="text-xs text-foreground-500 mb-4">
            As fotos são enviadas automaticamente para o Google Drive, dentro de "Catálogo BNI / {title.trim() || '(título do imóvel)'}".
          </p>

          {!driveAuthorized && (
            <div className="rounded-md border border-background-300/60 bg-background-100 p-5 mb-4 text-center">
              <p className="text-xs text-foreground-600 mb-3">
                Conecte sua conta do Google Drive para poder enviar fotos. Isso abre uma janela do Google — se nada
                aparecer, verifique se o navegador não bloqueou o pop-up (ex: Brave Shields).
              </p>
              <button
                type="button"
                onClick={handleConnectDrive}
                disabled={connectingDrive}
                className="inline-flex items-center gap-2 bg-secondary-900 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-secondary-800 transition-colors disabled:opacity-60"
              >
                {connectingDrive ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Conectando...</>
                ) : (
                  <><i className="ri-google-line"></i> Conectar ao Google Drive</>
                )}
              </button>
            </div>
          )}

          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative group aspect-square rounded-md overflow-hidden bg-background-200">
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                  {index === 0 && (
                    <span className="absolute top-1.5 left-1.5 bg-primary-500 text-background-50 text-[9px] font-bold px-1.5 py-0.5 rounded">
                      CAPA
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(photo)}
                    aria-label="Remover foto"
                    className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-secondary-950/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {driveAuthorized && (
            <label
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md py-8 cursor-pointer transition-colors ${
                uploading ? 'border-primary-300 bg-primary-50' : 'border-background-300/60 hover:border-primary-300 hover:bg-background-100'
              }`}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={uploading}
                onChange={(e) => handleFilesSelected(e.target.files)}
                className="hidden"
              />
              <i className={`text-2xl text-primary-500 ${uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-2-line'}`}></i>
              <span className="text-sm text-foreground-600">
                {uploading ? 'Enviando fotos para o Google Drive...' : 'Clique para escolher fotos'}
              </span>
            </label>
          )}
        </section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || uploading}
            className="flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin"></i> Salvando...</>
            ) : (
              <><i className="ri-save-line"></i> Salvar imóvel</>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="px-6 py-3 rounded-md text-sm font-semibold text-foreground-700 hover:bg-background-200 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>
    </main>
  );
}
